// src/widgets/core/index.ts
import { WidgetContainer } from './widget-container';
import { WidgetConfig } from '../types/config';
import { parseQueryParams } from '../utils/query-params';
import { persistStorageService } from '@/features/auth/persist-storage';

export class ConversationsWidget {
  private static instance: WidgetContainer | null = null;
  public static queue: any[] = [];
  public static isInitialized = false;

  /**
   * Initialize the widget in popup mode
   */
  static init(config: WidgetConfig) {
    // Clear any existing auth data first
    persistStorageService.batchRemovePersistItems({
      names: [
        'token',
        'refresh_token',
        'user',
        'organisation_id',
        'location_id',
      ],
    });
    // Apply default values
    const finalConfig = {
      parseQueryParams: true,
      position: 'right',
      ...config,
    } as WidgetConfig;

    // Process any query parameters if enabled
    const processedConfig = parseQueryParams(finalConfig);

    // Check if we should automatically open in full page mode
    if (processedConfig.isFullPage) {
      return this.initFull(processedConfig);
    }

    // Destroy any existing instance
    if (this.instance) {
      this.instance.destroy();
    }

    // Create new instance
    this.instance = new WidgetContainer(processedConfig);
    this.isInitialized = true;

    // Process any queued commands
    this.processQueue();

    // Auto-open if configured
    if (processedConfig.autoOpen || processedConfig.elementId) {
      this.open();
    }

    // Call onReady callback if provided
    if (typeof processedConfig.onReady === 'function') {
      processedConfig.onReady();
    }

    this.instance.updateOrganizationAndLocation(
      finalConfig.organisation_id,
      finalConfig.location_id,
    );

    if (finalConfig.access_token && finalConfig.refresh_token) {
      this.instance.setAuthToken(
        finalConfig.access_token,
        finalConfig.refresh_token,
      );
    }

    // Set API key if provided
    if (finalConfig.apiKey) {
      this.instance.setApiKey(finalConfig.apiKey);
    }

    return this.instance;
  }

  /**
   * Initialize the widget in full-page mode
   */
  static initFull(config: WidgetConfig) {
    const fullConfig = {
      ...config,
      isFullPage: true,
      parseQueryParams: config.parseQueryParams !== false,
    };

    // Process any query parameters if enabled
    const processedConfig = parseQueryParams(fullConfig);

    // Destroy any existing instance
    if (this.instance) {
      this.instance.destroy();
    }

    // Create new instance
    this.instance = new WidgetContainer(processedConfig);
    this.isInitialized = true;

    // Process any queued commands
    this.processQueue();

    // Call onReady callback if provided
    if (typeof processedConfig.onReady === 'function') {
      processedConfig.onReady();
    }

    return this.instance;
  }

  /**
   * Open the widget (for popup mode)
   */
  static open() {
    if (this.instance) {
      this.instance.open();

      // Call onOpen callback if provided
      const config = this.instance.getConfig();
      if (config && typeof config.onOpen === 'function') {
        config.onOpen();
      }
    } else {
      // Queue the open command for when init is called
      this.queue.push(['open']);
    }
  }

  /**
   * Close the widget (for popup mode)
   */
  static close() {
    if (this.instance) {
      this.instance.close();

      // Call onClose callback if provided
      const config = this.instance.getConfig();
      if (config && typeof config.onClose === 'function') {
        config.onClose();
      }
    } else {
      // Queue the close command for when init is called
      this.queue.push(['close']);
    }
  }

  /**
   * Completely remove the widget from the DOM
   */
  static destroy() {
    if (this.instance) {
      this.instance.destroy();
      this.instance = null;
    }
    this.isInitialized = false;
  }

  /**
   * Get the current widget instance
   */
  static getInstance() {
    return this.instance;
  }

  /**
   * Check if widget is initialized
   */
  static getIsInitialized() {
    return this.isInitialized;
  }

  /**
   * Process any queued commands that were called before initialization
   */
  private static processQueue() {
    while (this.queue.length > 0) {
      const [method, ...args] = this.queue.shift();
      if (typeof (this as any)[method] === 'function') {
        (this as any)[method](...args);
      }
    }
  }
}

// Global queue handler for when the script loads before the widget
if (typeof window !== 'undefined') {
  const w = window as any;
  const queueName = 'conversations';

  // Check if there's already a queue
  if (w[queueName] && Array.isArray(w[queueName].q)) {
    ConversationsWidget.queue = [...w[queueName].q];
  }

  // Set up the global ConversationsWidget object
  w.ConversationsWidget = ConversationsWidget;

  // Set up a command queue processor
  w[queueName] = function () {
    const args = Array.prototype.slice.call(arguments);
    if (args.length === 0) return;

    const method = args[0];
    const methodArgs = args.slice(1);

    if (method === 'init' || method === 'initFull') {
      (ConversationsWidget as any)[method](...methodArgs);
    } else if (
      ConversationsWidget.isInitialized &&
      typeof (ConversationsWidget as any)[method] === 'function'
    ) {
      (ConversationsWidget as any)[method](...methodArgs);
    } else {
      // Queue the command for later
      ConversationsWidget.queue.push([method, ...methodArgs]);
    }
  };
}
